// Utility functions shared across all portals

/**
 * Format currency in PKR
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount)
}

/**
 * Format a Firestore timestamp to a readable date string
 */
export function formatDate(timestamp: { toDate: () => Date } | Date | null | undefined): string {
    if (!timestamp) return 'N/A'

    const date = 'toDate' in timestamp ? timestamp.toDate() : timestamp
    return new Intl.DateTimeFormat('en-PK', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(date)
}

/**
 * Format a Firestore timestamp to a readable date and time string
 */
export function formatDateTime(timestamp: { toDate: () => Date } | Date | null | undefined): string {
    if (!timestamp) return 'N/A'

    const date = 'toDate' in timestamp ? timestamp.toDate() : timestamp
    return new Intl.DateTimeFormat('en-PK', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date)
}

/**
 * Get order status color for UI display
 */
export function getOrderStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
        pending: 'warning',
        confirmed: 'info',
        processing: 'primary',
        shipped: 'secondary',
        delivered: 'success',
        cancelled: 'error'
    }
    return colors[status] || 'default'
}

/**
 * Get payment status color for UI display
 */
export function getPaymentStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
        pending: 'warning',
        submitted: 'info',
        verified: 'success',
        failed: 'error'
    }
    return colors[status] || 'default'
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
}

/**
 * Generate a random order/invoice number
 */
export function generateInvoiceNumber(): string {
    const timestamp = Date.now().toString(36).toUpperCase()
    const random = Math.random().toString(36).substring(2, 6).toUpperCase()
    return `INV-${timestamp}-${random}`
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

/**
 * Validate Pakistani phone number
 */
export function isValidPakistaniPhone(phone: string): boolean {
    // Matches: 03XX-XXXXXXX, +923XXXXXXXXX, 923XXXXXXXXX
    const phoneRegex = /^(\+92|92|0)?3[0-9]{2}[-]?[0-9]{7}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.startsWith('92')) {
        return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`
    }
    if (cleaned.startsWith('0')) {
        return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`
    }
    return phone
}
